// M18 Zeitpläne – Liste, sowie Übersicht aller Zuweisungen (Gerät > Gruppe > Standort > Mandant, Kap. 7.1).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { can, useAssignments, useDevices, useGroups, useLocations, useMe, usePlaylists, useSchedules } from '../api/queries';
import type { Assignment, Schedule, TargetType } from '../api/types';
import { usePendingMap } from '../api/approvals';
import { PendingPill } from '../components/Approval';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Dialog, Empty, Field, PageHead, relativeTime, Spinner } from '../components/ui';
import { localeOf } from '../i18n';
import { AssignDialog } from './AssignDialog';
import { PowerSchedulesCard } from './Power';
import { hasFeature } from '../api/features';

export function SchedulesPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: schedules, isLoading } = useSchedules();
  const { data: playlists = [] } = usePlaylists();
  const navigate = useNavigate();
  const [create, setCreate] = useState(false);
  const manage = can(me, 'schedules.manage');
  const pending = usePendingMap();
  return (
    <>
      <PageHead title={t('nav.schedules')} sub={t('schedules.sub')}
        actions={manage && <button className="btn primary" onClick={() => setCreate(true)}><Icon name="plus" />{t('schedules.new')}</button>} />
      {isLoading ? <Spinner /> : !schedules?.length ? (
        <div className="card"><Empty icon="calendar" title={t('schedules.empty')} text={t('schedules.emptyText')}
          action={manage ? <button className="btn primary" onClick={() => setCreate(true)}><Icon name="plus" />{t('schedules.new')}</button> : undefined} /></div>
      ) : (
        <div className="card scroll" style={{ padding: 10 }}>
          <table className="list">
            <thead><tr><th>{t('common.name')}</th><th>{t('schedules.rules')}</th><th>{t('schedules.fallback')}</th><th>{t('schedules.usedBy')}</th><th>{t('playlists.changed')}</th></tr></thead>
            <tbody>
              {schedules.map((s: Schedule) => (
                <tr key={s.id} className="click" onClick={() => navigate(`/schedules/${s.id}`)}>
                  <td><b>{s.name}</b> <PendingPill request={pending.get(s.id)} /></td>
                  <td>{s.ruleCount}</td>
                  <td className="muted">{playlists.find((p) => p.id === s.fallbackPlaylistId)?.name ?? '–'}</td>
                  <td className="muted">{t('schedules.usedByCount', { count: s.usedBy })}</td>
                  <td className="muted">{relativeTime(s.updatedAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AssignmentsCard />
      {hasFeature(me, 'power') && <PowerSchedulesCard />}
      {create && <CreateScheduleDialog onClose={() => setCreate(false)} onCreated={(id) => navigate(`/schedules/${id}`)} />}
    </>
  );
}

/** Alle Zuweisungen des Mandanten; Standard (Mandant) direkt setzbar. */
export function AssignmentsCard({ scheduleId }: { scheduleId?: string }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: assignments = [] } = useAssignments();
  const { data: playlists = [] } = usePlaylists();
  const { data: schedules = [] } = useSchedules();
  const { data: devices = [] } = useDevices();
  const { data: groups = [] } = useGroups();
  const { data: locations = [] } = useLocations();
  const [edit, setEdit] = useState<{ targetType: TargetType; targetId: string | null } | null>(null);
  const [add, setAdd] = useState(false);
  const manage = can(me, 'assignments.manage');
  const order: TargetType[] = ['tenant', 'location', 'group', 'device'];
  const list = assignments.filter((a) => !scheduleId || a.scheduleId === scheduleId)
    .sort((a, b) => order.indexOf(a.targetType) - order.indexOf(b.targetType));
  const targetName = (a: Pick<Assignment, 'targetType' | 'targetId'>) => a.targetType === 'tenant' ? t('assign.allScreens')
    : a.targetType === 'location' ? locations.find((l) => l.id === a.targetId)?.name
    : a.targetType === 'group' ? groups.find((g) => g.id === a.targetId)?.name
    : devices.find((d) => d.id === a.targetId)?.name;
  const contentName = (a: Assignment) => a.playlistId ? playlists.find((p) => p.id === a.playlistId)?.name : schedules.find((s) => s.id === a.scheduleId)?.name;
  const hasTenant = assignments.some((a) => a.targetType === 'tenant');

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 className="grow" style={{ margin: 0 }}>{scheduleId ? t('schedules.assignedTo') : t('assign.overview')}</h2>
        {manage && !scheduleId && !hasTenant && <button className="btn sm" onClick={() => setEdit({ targetType: 'tenant', targetId: null })}>{t('assign.setDefault')}</button>}
        {manage && scheduleId && <button className="btn sm" onClick={() => setAdd(true)}><Icon name="plus" />{t('schedules.assign')}</button>}
      </div>
      {!scheduleId && <div className="muted small" style={{ marginBottom: 8 }}>{t('assign.precedence')}</div>}
      {list.length ? (
        <ul className="list-plain">
          {list.map((a) => (
            <li key={a.id}>
              <span className="pill neutral plain">{t(`assign.via.${a.targetType}`)}</span>
              <b className="grow">{targetName(a) ?? '–'}</b>
              <span className="muted small">{a.scheduleId ? <><Icon name="calendar" /> </> : <><Icon name="list" /> </>}{contentName(a) ?? '–'}</span>
              {manage && <button className="btn sm ghost" onClick={() => setEdit({ targetType: a.targetType, targetId: a.targetId })}>{t('common.change')}</button>}
            </li>
          ))}
        </ul>
      ) : <div className="muted">{t('assign.none')}</div>}
      {edit && <AssignDialog targets={[edit]} title={`${t('assign.title')}: ${targetName(edit) ?? ''}`} onClose={() => setEdit(null)} />}
      {add && scheduleId && <TargetDialog scheduleId={scheduleId} onClose={() => setAdd(false)} />}
    </div>
  );
}

function TargetDialog({ scheduleId, onClose }: { scheduleId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: devices = [] } = useDevices();
  const { data: groups = [] } = useGroups();
  const { data: locations = [] } = useLocations();
  const { refetch } = useAssignments();
  const [type, setType] = useState<TargetType>('location');
  const [target, setTarget] = useState('');
  const options = type === 'location' ? locations : type === 'group' ? groups : type === 'device' ? devices.filter((d) => d.status === 'active') : [];
  const save = async () => {
    try {
      await api.put('/assignments', { targetType: type, targetId: type === 'tenant' ? null : target, scheduleId });
      await refetch();
      toast.show(t('assign.saved'));
      onClose();
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={t('schedules.assign')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={type !== 'tenant' && !target} onClick={save}>{t('common.save')}</button>
    </>}>
      <Field label={t('assign.target')}>
        <select value={type} onChange={(e) => { setType(e.target.value as TargetType); setTarget(''); }}>
          {(['tenant', 'location', 'group', 'device'] as TargetType[]).map((x) => <option key={x} value={x}>{t(`assign.via.${x}`)}</option>)}
        </select>
      </Field>
      {type !== 'tenant' && (
        <Field label={t(`assign.via.${type}`)}>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">{t('design.choose')}</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </Field>
      )}
      <div className="hint">{t('assign.replaceHint')}</div>
    </Dialog>
  );
}

function CreateScheduleDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data: playlists = [] } = usePlaylists();
  const [name, setName] = useState('');
  const [fallback, setFallback] = useState('');
  const submit = async () => {
    try {
      const s = await api.post<Schedule>('/schedules', { name: name.trim(), fallbackPlaylistId: fallback || null });
      onCreated(s.id);
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={t('schedules.new')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!name.trim()} onClick={submit}>{t('common.create')}</button>
    </>}>
      <form className="col" onSubmit={(e) => { e.preventDefault(); if (name.trim()) void submit(); }}>
        <Field label={t('common.name')}><input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t('schedules.namePh')} /></Field>
        <Field label={t('schedules.fallback')} hint={t('schedules.fallbackHint')}>
          <select value={fallback} onChange={(e) => setFallback(e.target.value)}>
            <option value="">{t('schedules.noFallback')}</option>
            {playlists.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      </form>
    </Dialog>
  );
}
